<script setup lang="ts">
import { onMounted, onUnmounted, shallowRef, type CSSProperties } from "vue";
import type { Map, MapMouseEvent, StyleSpecification } from "mapbox-gl";
import type { FeatureCollection } from "geojson";
import "mapbox-gl/dist/mapbox-gl.css";

import { BUTTON_FOCUS_DARK } from "../nuxt/ui-theme";
import { activeFeatureRevealOffset, zoomToRevealPointAtOffset } from "../utils/mapZoom";
import { bottomSlotFramingInset } from "../utils/mapFraming";
import { MAP_PIN_IMAGE_URL, MAP_PIN_WIDTH } from "../utils/mapPinImage";
import { GROUP_MIN_ZOOM } from "../composables/useAppMapCluster";

defineOptions({ inheritAttrs: false });

const mapboxAccessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
const mapboxUsername = import.meta.env.VITE_MAPBOX_USERNAME;
const mapboxStyleId = import.meta.env.VITE_MAPBOX_STYLE_ID;

const remoteStyleUrl = `mapbox://styles/${mapboxUsername}/${mapboxStyleId}?optimize=true`;

// Bascule Mapbox sur des glyphes générés par le client : sinon les compteurs de clusters, dont les
// PBF viennent du réseau, resteraient vides hors-ligne.
const OFFLINE_LABEL_FONT_FAMILY = "Averta, sans-serif";
const OFFLINE_SEA_COLOR = "#28395b";
const OFFLINE_LAND_COLOR = "#30609c";
const SEA_LAYER = "offline-sea";
const LAND_SOURCE = "offline-land";
const LAND_LAYER = "offline-land-fill";

let landPromise: Promise<FeatureCollection> | undefined;
function loadLand() {
  landPromise ??= import("../geo/land-50m.geo.json").then((m) => m.default as unknown as FeatureCollection);
  return landPromise;
}

async function buildOfflineStyle(): Promise<StyleSpecification> {
  return {
    version: 8,
    glyphs: "mapbox://fonts/mapbox/{fontstack}/{range}.pbf",
    sources: { [LAND_SOURCE]: { type: "geojson", data: await loadLand() } },
    layers: [
      { id: SEA_LAYER, type: "background", paint: { "background-color": OFFLINE_SEA_COLOR } },
      { id: LAND_LAYER, type: "fill", source: LAND_SOURCE, paint: { "fill-color": OFFLINE_LAND_COLOR } },
    ],
  };
}

async function styleFor(isOnline: boolean) {
  return isOnline ? remoteStyleUrl : await buildOfflineStyle();
}

function labelFontFor(isOnline: boolean) {
  return isOnline ? undefined : OFFLINE_LABEL_FONT_FAMILY;
}

const toast = useToast();

const mapboxMap = shallowRef<Map>();

const emit = defineEmits<{
  init: [map: Map];
  moveEnd: [];
  markerClick: [payload: { feature: GeoJSON.Feature; event: MapMouseEvent }];
  fullscreenChange: [value: boolean];
}>();

const props = withDefaults(
  defineProps<{
    markers?: FeatureCollection;
    mode?: "markers" | "cluster" | "hexgrid";
    clusterCountProperty?: string;
    activeFeatureId?: number | string;
    center?: [number, number];
    // Zoom initial (lu une seule fois au montage). Absent → zoom plancher où le monde remplit le
    // viewport (comportement historique). Fourni → borné à [zoom plancher, zoom max] pour rouvrir la
    // carte sur le cadrage de la carte appelante plutôt que sur le monde entier.
    zoom?: number;
    zoomButtons?: boolean;
    fullscreenButton?: boolean;
    labelledFullscreenButton?: boolean;
    geolocateButton?: boolean;
    // Estompe (fondu + non-interactif) les commandes du bas — géoloc, zoom, slot #bottom — sans
    // toucher le plein écran : utilisé quand un panneau bas (sheet d'aperçu) s'ouvre par-dessus.
    controlsDimmed?: boolean;
    interactive?: boolean;
    cooperativeGestures?: boolean;
    bottomPadding?: number;
    // Marge (px) du cadrage initial sur les marqueurs (fitMarkers), sur les 4 côtés. À 0, le monde
    // peut remplir entièrement le cadre au zoom plancher ; sinon la marge réservée laisse voir le
    // fond (bande) dès que le cadrage embrasse le monde entier. Le bottom est étendu par bottomPadding.
    fitPadding?: number;
    // Consentement Mapbox injecté par l'hôte (web : cookie-consent ; mobile : toujours
    // autorisé en natif). Quand false, la carte n'est pas initialisée et le slot #disabled
    // est rendu à la place.
    mapboxAllowed?: boolean;
    // Injecté par l'hôte (mobile : useNetworkStatus ; web : laissé à true, rien n'y est consultable
    // hors-ligne). À false, la carte perd son fond et garde marqueurs, layers et gestes.
    online?: boolean;
    // Cadre la vue sur les marqueurs au chargement (et tant qu'aucun marqueur n'était encore
    // disponible). Le web pilote plutôt le cadrage via useMapInitialFocus / useMapLocationFocus.
    fitMarkers?: boolean;
    fitMaxZoom?: number;
    // Au montage, cadre (jumpTo, sans animation) sur le marqueur déjà actif plutôt que sur l'ensemble.
    focusActiveOnInit?: boolean;
    // Cible du Teleport plein écran : web `body`, mobile `ion-app` (pour rester sous les IonModal).
    fullscreenTeleportTo?: string;
    // Feuille #pinned : réserve `bottomPadding` en padding interne (safe-area sous la carte) au lieu du décalage flottant par défaut.
    pinnedSafeArea?: boolean;
  }>(),
  {
    markers: () => ({
      type: "FeatureCollection" as const,
      features: [],
    }),
    mode: "markers",
    center: () => [0, 20],
    zoomButtons: true,
    fullscreenButton: true,
    labelledFullscreenButton: false,
    geolocateButton: true,
    interactive: true,
    cooperativeGestures: true,
    bottomPadding: 0,
    fitPadding: 48,
    controlsDimmed: false,
    mapboxAllowed: true,
    online: true,
    fitMarkers: false,
    fitMaxZoom: 13,
    focusActiveOnInit: false,
    fullscreenTeleportTo: "body",
    pinnedSafeArea: false,
  }
);

const { t } = useI18n();
const mapboxAllowed = toRef(props, "mapboxAllowed");
const online = toRef(props, "online");

const containerRef = useTemplateRef("containerRef");
const rootRef = useTemplateRef("rootRef");

const activeFeatureIdRef = toRef(props, "activeFeatureId");

const zoomLevel = ref(0);
const isFullscreen = defineModel<boolean>("fullscreen", { default: false });

function toggleFullscreen() {
  isFullscreen.value = !isFullscreen.value;
}

useAppEscapeKey(
  () => {
    isFullscreen.value = false;
  },
  { priority: 100, when: isFullscreen }
);

// `route.path` et non `fullPath` : une modale URL (`?modal=`) ouverte depuis la carte ne doit pas quitter le plein écran.
const route = useRoute();
watch(
  () => route.path,
  () => {
    isFullscreen.value = false;
  }
);

watch(isFullscreen, async (v) => {
  await nextTick();
  const m = mapboxMap.value;
  if (m) {
    m.resize();
    constrainLatitudeToWorld(m);
    m.setCooperativeGestures(props.cooperativeGestures && !v);
  }
  emit("fullscreenChange", v);
});

watch(
  () => props.cooperativeGestures,
  (v) => {
    mapboxMap.value?.setCooperativeGestures(v && !isFullscreen.value);
  }
);

// Taille du monde Mercator au zoom 0 (tuiles Mapbox = 512 px). DOIT rester ≤ 512 : c'est le seuil
// où le monde remplit déjà la plus grande dimension du conteneur. Au-dessus, on autorise un dézoom
// qui découvre une bande de fond sous le monde (Mercator ne se répète pas verticalement).
const MERCATOR_WORLD_PX = 512;
// Mapbox rend une fine bande d'artefact (couleur d'atmosphère, ~8px à l'écran) tout au bord nord/sud
// du monde Mercator, visible dès qu'on pan jusqu'au bord. On empêche le viewport de l'atteindre :
// `constrainLatitudeToWorld` tient le bord du viewport à EDGE_BAND_PX du bord du monde (à tout zoom).
// Au cadrage « monde entier » (zoom plancher), le viewport ne peut plus reculer — le monde remplit
// déjà la hauteur — donc le cadrage sur-couvre de EDGE_BAND_PX de chaque côté (d'où le ×2) pour
// pousser les deux bandes hors champ. Surcoût : un zoom monde à peine plus serré (latitudes extrêmes
// rognées, sans incidence pour des sites de plongée).
const EDGE_BAND_PX = 12;
function computeMinFitZoom() {
  const container = containerRef.value;
  if (!container) return 0;
  const size = Math.max(container.clientWidth, container.clientHeight);
  if (size <= 0) return 0;
  return Math.max(0, Math.log2((size + 2 * EDGE_BAND_PX) / MERCATOR_WORLD_PX));
}
function applyMinFitZoom() {
  const m = mapboxMap.value;
  if (!m) return;
  const minZoom = computeMinFitZoom();
  if (Math.abs(m.getMinZoom() - minZoom) > 0.001) {
    m.setMinZoom(minZoom);
  }
}

// Borne la latitude du centre pour que les bords haut/bas du viewport restent à EDGE_BAND_PX du bord
// du monde Mercator (y ∈ [0, 1], 0 = bord nord, 1 = bord sud), masquant la bande d'artefact du bord.
// La marge est exprimée en pixels écran (constante via la taille du monde au zoom courant), donc le
// recul reste identique quel que soit le zoom. On NE touche que la latitude : `maxBounds` bornerait
// aussi la longitude (clamp natif bi-axes) et tuerait le défilement est-ouest infini qu'on veut garder.
// L'écriture passe par `transform.center` plutôt que `setCenter`/`jumpTo` : ces derniers font un
// `stop()` qui interromprait le geste de drag (« le pan s'arrête »), alors qu'écrire la transform
// clampe en continu pendant le drag, sans rebond. API interne → dégradé proprement si absente.
let constrainingLatitude = false;
function constrainLatitudeToWorld(m: Map) {
  if (constrainingLatitude || !mapboxglRef) return;
  const transform = (m as unknown as { transform?: { center: unknown } }).transform;
  if (!transform) return;
  const height = containerRef.value?.clientHeight;
  if (!height) return;
  const worldPx = MERCATOR_WORLD_PX * 2 ** m.getZoom();
  const margin = EDGE_BAND_PX / worldPx;
  const halfSpan = height / 2 / worldPx;
  let lo = halfSpan + margin;
  let hi = 1 - halfSpan - margin;
  if (lo > hi) lo = hi = 0.5; // monde plus court que le viewport : on centre
  const center = m.getCenter();
  const y = mapboxglRef.MercatorCoordinate.fromLngLat(center).y;
  const clampedY = Math.min(hi, Math.max(lo, y));
  if (Math.abs(clampedY - y) < 1e-9) return;
  const lat = new mapboxglRef.MercatorCoordinate(0, clampedY).toLngLat().lat;
  constrainingLatitude = true;
  transform.center = new mapboxglRef.LngLat(center.lng, lat);
  (m as unknown as { _update?: () => void })._update?.();
  constrainingLatitude = false;
}

defineExpose({ mapboxMap, isFullscreen, focusFeature });

const canZoomIn = computed(() => {
  const m = mapboxMap.value;
  return m ? zoomLevel.value < m.getMaxZoom() : false;
});

const canZoomOut = computed(() => {
  const m = mapboxMap.value;
  return m ? zoomLevel.value > m.getMinZoom() : false;
});

// Estompe les commandes du bas : `invisible` gère à la fois le fondu (différé en fin de transition)
// et le retrait des clics/focus, fiable même sur les boutons qui forcent `pointer-events-auto`.
const controlsDimClass = computed(() => (props.controlsDimmed ? "opacity-0 invisible" : "opacity-100 visible"));

const labelledFullscreen = computed(() => props.fullscreenButton && props.labelledFullscreenButton && !isFullscreen.value);

const pinnedContainerStyle = computed<CSSProperties>(() => {
  const value = isFullscreen.value ? "0px" : `${props.bottomPadding}px`;
  return props.pinnedSafeArea ? { "--app-map-pinned-inset": value } : { bottom: "0px", "--app-map-pinned-inset": value };
});

function zoomIn() {
  mapboxMap.value?.zoomIn();
}

function zoomOut() {
  mapboxMap.value?.zoomOut();
}

const geolocating = ref(false);
const geolocationActive = ref(false);
const geolocationAvailable = ref(false);
let userLocationMarker: import("mapbox-gl").Marker | undefined;

onMounted(() => {
  geolocationAvailable.value = typeof navigator !== "undefined" && "geolocation" in navigator && window.isSecureContext;
});

function geolocate() {
  const m = mapboxMap.value;
  if (!m || !mapboxglRef || !geolocationAvailable.value) return;

  if (geolocationActive.value) {
    userLocationMarker?.remove();
    userLocationMarker = undefined;
    geolocationActive.value = false;
    return;
  }

  if (geolocating.value) return;

  geolocating.value = true;
  navigator.geolocation.getCurrentPosition(
    (position) => {
      const { longitude, latitude } = position.coords;
      geolocating.value = false;
      geolocationActive.value = true;
      m.flyTo({ center: [longitude, latitude], zoom: 13 });
      userLocationMarker?.remove();
      const el = document.createElement("div");
      el.className = "mapboxgl-user-location-dot";
      userLocationMarker = new mapboxglRef.Marker({ element: el }).setLngLat([longitude, latitude]).addTo(m);
    },
    (error) => {
      geolocating.value = false;
      geolocationActive.value = false;
      const kind = error.code === error.PERMISSION_DENIED ? "denied" : error.code === error.TIMEOUT ? "timeout" : "unavailable";
      toast.add({
        id: `geolocation-${kind}`,
        title: t(`toast.geolocation.${kind}.title`),
        description: t(`toast.geolocation.${kind}.description`),
        color: "error",
      });
    },
    { enableHighAccuracy: true, timeout: 10000 }
  );
}

function scrollMapIntoView() {
  if (isFullscreen.value || !rootRef.value) return;
  const rect = rootRef.value.getBoundingClientRect();
  const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--ui-header-height")) || 72;
  const delta = rect.top - headerHeight;
  if (delta > 1) {
    window.scrollBy({ top: delta, behavior: "smooth" });
  }
}

function previewRecenterOffset(): [number, number] {
  if (typeof window === "undefined") return [0, 0];
  const mapHeight = mapboxMap.value?.getContainer().getBoundingClientRect().height ?? window.innerHeight;
  return activeFeatureRevealOffset(mapHeight, window.innerWidth);
}

function centerOnFeature(feature: GeoJSON.Feature, offset: [number, number] = [0, 0]) {
  const m = mapboxMap.value;
  if (!m || feature.geometry.type !== "Point") return;
  const center = feature.geometry.coordinates as [number, number];
  const viewportHeight = m.getContainer().getBoundingClientRect().height;
  const revealZoom = zoomToRevealPointAtOffset(center[1], offset[1], viewportHeight);
  const zoom = Math.min(m.getMaxZoom(), Math.max(m.getZoom(), revealZoom));
  m.easeTo({ center, offset, zoom, duration: 500 });
}

function focusFeature(id: number | string): void {
  const feature = markersRef.value.features.find((f) => String(f.properties?.id) === String(id));
  if (feature) nextTick(() => centerOnFeature(feature, previewRecenterOffset()));
}

const markersRef = toRef(props, "markers");

const vizOptions = {
  data: markersRef,
  activeFeatureId: activeFeatureIdRef,
  localGlyphs: computed(() => !online.value),
  onFeatureClick: (payload: { feature: GeoJSON.Feature; event: MapMouseEvent }) => {
    scrollMapIntoView();
    emit("markerClick", payload);
    nextTick(() => centerOnFeature(payload.feature, previewRecenterOffset()));
  },
};

let spiderfiedPositions: ReturnType<typeof useAppMapCluster>["spiderfiedPositions"] | undefined;

if (props.mode === "cluster") {
  ({ spiderfiedPositions } = useAppMapCluster(mapboxMap, { ...vizOptions, countProperty: props.clusterCountProperty }));
} else if (props.mode === "hexgrid") {
  useAppMapHexgrid(mapboxMap, vizOptions);
} else {
  useAppMapMarkers(mapboxMap, vizOptions);
}

useAppMapActivePin(mapboxMap, {
  data: markersRef,
  activeFeatureId: activeFeatureIdRef,
  imageUrl: MAP_PIN_IMAGE_URL,
  width: MAP_PIN_WIDTH,
  positionOverrides: spiderfiedPositions,
  hiddenFromZoom: props.mode === "cluster" ? GROUP_MIN_ZOOM : undefined,
});

let mapboxglRef: any;

// Cadrage initial sur les marqueurs (opt-in via `fitMarkers`). On ne recadre pas à chaque
// changement de données — uniquement tant qu'aucun marqueur n'avait encore pu être cadré
// (les données peuvent arriver après le chargement de la carte) — pour éviter les sauts au filtrage.
let didFit = false;

function bottomFramingInset(): number {
  return bottomSlotFramingInset(containerRef.value, props.bottomPadding || props.fitPadding);
}

function fitToMarkers(): boolean {
  const m = mapboxMap.value;
  if (!m || !mapboxglRef) return false;
  const bounds = new mapboxglRef.LngLatBounds();
  let hasPoint = false;
  for (const f of props.markers.features) {
    if (f.geometry.type !== "Point") continue;
    const [lng, lat] = f.geometry.coordinates;
    // Une coordonnée absente ou hors bornes ferait lever `extend` (LngLatBounds) et
    // interromprait le rendu de la carte : on l'ignore plutôt que de tout casser.
    if (lng == null || lat == null) continue;
    if (!Number.isFinite(lng) || !Number.isFinite(lat) || lat < -90 || lat > 90 || lng < -180 || lng > 180) continue;
    bounds.extend([lng, lat]);
    hasPoint = true;
  }
  if (!hasPoint) return false;
  m.fitBounds(bounds, {
    padding: { top: props.fitPadding, right: props.fitPadding, bottom: bottomFramingInset(), left: props.fitPadding },
    maxZoom: props.fitMaxZoom,
    animate: false,
  });
  return true;
}

function focusActiveFeature(): boolean {
  const m = mapboxMap.value;
  const id = activeFeatureIdRef.value;
  if (!m || id == null) return false;
  const feature = props.markers.features.find((f) => String(f.properties?.id) === String(id));
  if (!feature || feature.geometry.type !== "Point") return false;
  const [lng, lat] = feature.geometry.coordinates;
  if (lng == null || lat == null || !Number.isFinite(lng) || !Number.isFinite(lat)) return false;
  m.easeTo({ center: [lng, lat], zoom: props.fitMaxZoom, offset: previewRecenterOffset(), duration: 0 });
  return true;
}

function applyInitialFraming(): boolean {
  if (props.focusActiveOnInit && focusActiveFeature()) return true;
  return props.fitMarkers ? fitToMarkers() : false;
}

watch(markersRef, () => {
  if (didFit || !mapboxMap.value) return;
  if (props.focusActiveOnInit || props.fitMarkers) didFit = applyInitialFraming();
});

let resizeObserver: ResizeObserver | undefined;
let initialized = false;

async function initMap() {
  if (initialized) return;
  if (!mapboxAllowed.value) return;
  if (!containerRef.value) return;
  initialized = true;

  const mapboxgl = (await import("mapbox-gl")).default;
  mapboxglRef = mapboxgl;

  const wasOnline = online.value;
  const style = await styleFor(wasOnline);

  if (!containerRef.value) {
    initialized = false;
    return;
  }

  mapboxgl.accessToken = mapboxAccessToken;

  // Zoom plancher où le monde remplit déjà le viewport : à zoom 0 le monde est plus petit que la
  // carte, Mapbox interdit les bords vides et écrase la latitude du centre vers l'équateur.
  const MAX_ZOOM = 17;
  const minFitZoom = computeMinFitZoom();
  // Zoom initial : celui demandé (borné) ou, par défaut, le plancher.
  const initialZoom = props.zoom != null ? Math.min(MAX_ZOOM, Math.max(minFitZoom, props.zoom)) : minFitZoom;
  const map = new mapboxgl.Map({
    container: containerRef.value,
    style,
    localFontFamily: labelFontFor(wasOnline),
    projection: "mercator",
    renderWorldCopies: true,
    attributionControl: false,
    pitchWithRotate: false,
    fadeDuration: 0,
    center: props.center,
    zoom: initialZoom,
    minZoom: minFitZoom,
    maxZoom: MAX_ZOOM,
    cooperativeGestures: props.cooperativeGestures && !isFullscreen.value,
    locale: {
      "ScrollZoomBlocker.CtrlMessage": t("map.gesture.scroll_ctrl"),
      "ScrollZoomBlocker.CmdMessage": t("map.gesture.scroll_cmd"),
      "TouchPanBlocker.Message": t("map.gesture.touch_pan"),
    },
    transformRequest: (url, resourceType) => {
      if (resourceType === "Glyphs" && mapboxUsername) {
        return { url: url.replace("/fonts/v1/mapbox/", `/fonts/v1/${mapboxUsername}/`) };
      }
      return { url };
    },
  });

  mapboxMap.value = map;
  zoomLevel.value = map.getZoom();

  if (online.value !== wasOnline) void applyStyle(online.value);

  map.touchPitch.disable();
  map.dragRotate.disable();
  map.touchZoomRotate.disableRotation();

  map.on("zoom", () => {
    zoomLevel.value = map.getZoom();
  });

  // Clamp en continu (y compris pendant le drag) : l'écriture directe de la transform ne coupe pas le
  // geste, donc pas besoin de le suspendre pendant le drag.
  map.on("move", () => constrainLatitudeToWorld(map));

  map.on("moveend", () => {
    emit("moveEnd");
  });

  let pendingResize = false;
  resizeObserver = new ResizeObserver(() => {
    if (pendingResize) return;
    pendingResize = true;
    requestAnimationFrame(() => {
      pendingResize = false;
      // Onglet masqué (Ionic pose display:none sur la page inactive) → conteneur 0×0 : redimensionner
      // à cette taille fige le canvas à zéro et, au retour sur l'onglet, il est étiré (carte déformée
      // verticalement) le temps d'un nouveau resize. On ignore les dimensions nulles pour préserver
      // la dernière taille valide.
      const el = containerRef.value;
      if (!el || !el.clientWidth || !el.clientHeight) return;
      map.resize();
      applyMinFitZoom();
      constrainLatitudeToWorld(map);
    });
  });
  resizeObserver.observe(containerRef.value);

  map.on("load", () => {
    applyMinFitZoom();
    if (!didFit && (props.focusActiveOnInit || props.fitMarkers)) didFit = applyInitialFraming();
    emit("init", map);
  });
}

onMounted(initMap);

const STYLE_SWAP_SNAPSHOT_FADE_MS = 300;
const STYLE_SWAP_SNAPSHOT_MAX_HOLD_MS = 15000;

let removeStyleSwapSnapshot: (() => void) | undefined;

// Sans `preserveDrawingBuffer`, le buffer WebGL est vidé après compositing : capture pendant `render`.
function captureCanvasSnapshot(m: Map): Promise<string | undefined> {
  return new Promise((resolve) => {
    let captured = false;
    const capture = () => {
      if (captured) return;
      captured = true;
      m.off("render", capture);
      try {
        resolve(m.getCanvas().toDataURL());
      } catch {
        resolve(undefined);
      }
    };
    m.once("render", capture);
    setTimeout(capture, 300);
    m.triggerRepaint();
  });
}

function insertOverCanvasUnderMarkers(container: HTMLElement, image: HTMLImageElement, canvas: HTMLCanvasElement) {
  container.insertBefore(image, canvas.nextSibling);
}

function holdSnapshotUntilMapIdle(m: Map, snapshot: string | undefined) {
  removeStyleSwapSnapshot?.();
  if (!snapshot) return;
  const canvas = m.getCanvas();
  const container = canvas.parentElement;
  if (!container) return;

  const image = document.createElement("img");
  image.src = snapshot;
  image.alt = "";
  image.style.cssText = `position:absolute;inset:0;width:100%;height:100%;object-fit:cover;pointer-events:none;transition:opacity ${STYLE_SWAP_SNAPSHOT_FADE_MS}ms`;
  insertOverCanvasUnderMarkers(container, image, canvas);

  const fadeOut = () => {
    detach();
    image.style.opacity = "0";
    setTimeout(() => image.remove(), STYLE_SWAP_SNAPSHOT_FADE_MS);
  };
  const maxHoldTimeout = setTimeout(fadeOut, STYLE_SWAP_SNAPSHOT_MAX_HOLD_MS);
  const detach = () => {
    clearTimeout(maxHoldTimeout);
    m.off("idle", fadeOut);
    removeStyleSwapSnapshot = undefined;
  };
  m.once("idle", fadeOut);
  removeStyleSwapSnapshot = () => {
    detach();
    image.remove();
  };
}

// `diff` par défaut réémet `styledata` mais pas `style.load` : les layers ne seraient jamais reposés.
// `localIdeographFontFamily` est typé requis à tort par mapbox-gl, d'où son passage explicite.
async function applyStyle(isOnline: boolean) {
  const style = await styleFor(isOnline);
  if (online.value !== isOnline) return;
  const m = mapboxMap.value;
  if (!m) return;
  const snapshot = await captureCanvasSnapshot(m);
  if (online.value !== isOnline || mapboxMap.value !== m) return;
  m.setStyle(style, {
    diff: false,
    localFontFamily: labelFontFor(isOnline),
    localIdeographFontFamily: undefined,
  });
  holdSnapshotUntilMapIdle(m, snapshot);
}

watch(online, (value) => void applyStyle(value));

watch(mapboxAllowed, async (allowed) => {
  if (!allowed) {
    mapboxMap.value?.remove();
    mapboxMap.value = undefined;
    resizeObserver?.disconnect();
    resizeObserver = undefined;
    initialized = false;
    return;
  }
  await nextTick();
  initMap();
});

onUnmounted(() => {
  removeStyleSwapSnapshot?.();
  userLocationMarker?.remove();
  resizeObserver?.disconnect();
  mapboxMap.value?.remove();
});
</script>

<template>
  <div v-if="!mapboxAllowed" v-bind="$attrs">
    <slot name="disabled" />
  </div>
  <Teleport v-else :to="fullscreenTeleportTo" :disabled="!isFullscreen">
    <div
      ref="rootRef"
      v-bind="$attrs"
      class="group/map @container/map @container-size"
      :class="[isFullscreen ? 'fixed! h-dvh! w-full! rounded-none! inset-0 z-50' : 'relative', 'overflow-hidden bg-[#28395b]']"
      :role="isFullscreen ? 'dialog' : undefined"
      :aria-modal="isFullscreen ? 'true' : undefined"
      :aria-label="isFullscreen ? t('map.fullscreen.label') : undefined"
    >
      <div ref="containerRef" class="size-full" :style="{ pointerEvents: interactive ? undefined : 'none' }" />
      <div
        data-item="map-overlay"
        class="absolute inset-0 group-aria-modal/map:py-safe-or-2 z-1 pointer-events-none"
        :style="{ 'margin-bottom': !isFullscreen ? `${bottomPadding}px` : '0' }"
      >
        <div class="size-full flex flex-col gap-6 py-6 group-aria-modal/map:py-4 px-4 @2xl/map:px-6">
          <div class="flex gap-3 @2xl/map:gap-6 grow min-h-0">
            <div v-if="$slots.default || $slots.bottomLeft" class="flex flex-col gap-3 @2xl/map:gap-6 grow min-h-0">
              <div v-if="$slots.default" class="grow min-h-0 *:pointer-events-auto *:max-h-full">
                <slot :is-fullscreen="isFullscreen" />
              </div>
              <div v-if="$slots.bottomLeft" class="mt-auto *:pointer-events-auto">
                <slot name="bottomLeft" :is-fullscreen="isFullscreen" />
              </div>
            </div>
            <div v-if="fullscreenButton || zoomButtons || geolocateButton" class="flex flex-col gap-3 @2xl/map:gap-4 ml-auto">
              <div v-if="fullscreenButton && !labelledFullscreen" class="grow min-h-0">
                <UButton
                  :class="`pointer-events-auto p-3 ${BUTTON_FOCUS_DARK}`"
                  :icon="isFullscreen ? 'icon:close' : 'icon:fullscreen'"
                  color="neutral"
                  variant="soft"
                  :aria-label="isFullscreen ? t('map.fullscreen.exit') : t('map.fullscreen.enter')"
                  :aria-pressed="isFullscreen"
                  @click="toggleFullscreen"
                  square
                />
              </div>
              <div
                v-if="zoomButtons || geolocateButton"
                class="mt-auto flex flex-col gap-3 @2xl/map:gap-4 transition-all duration-100"
                :class="controlsDimClass"
              >
                <div v-if="geolocateButton">
                  <UButton
                    data-testid="map-geolocate"
                    :class="`pointer-events-auto p-3 ${BUTTON_FOCUS_DARK}`"
                    icon="icon:crosshair"
                    :color="geolocationActive ? 'secondary' : 'neutral'"
                    :variant="geolocationActive ? 'solid' : 'soft'"
                    :loading="geolocating"
                    :disabled="!geolocationAvailable"
                    :aria-pressed="geolocationActive"
                    :aria-label="t('map.geolocate')"
                    @click="geolocate"
                    square
                  />
                </div>
                <div v-if="zoomButtons" class="flex flex-col gap-1" role="group" :aria-label="t('map.zoom.label')">
                  <UButton
                    :class="`pointer-events-auto p-3 ${BUTTON_FOCUS_DARK}`"
                    icon="icon:zoom-plus"
                    color="neutral"
                    variant="soft"
                    :disabled="!canZoomIn"
                    :aria-label="t('map.zoom.in')"
                    @click="zoomIn"
                    square
                  />
                  <UButton
                    :class="`pointer-events-auto p-3 ${BUTTON_FOCUS_DARK}`"
                    icon="icon:zoom-minus"
                    color="neutral"
                    variant="soft"
                    :disabled="!canZoomOut"
                    :aria-label="t('map.zoom.out')"
                    @click="zoomOut"
                    square
                  />
                </div>
              </div>
              <div v-if="labelledFullscreen" class="mt-auto">
                <UButton
                  :class="`pointer-events-auto ${BUTTON_FOCUS_DARK}`"
                  :label="t('map.fullscreen.browse')"
                  trailing-icon="icon:fullscreen"
                  color="neutral"
                  variant="soft"
                  @click="toggleFullscreen"
                />
              </div>
            </div>
          </div>
          <div data-app-map-bottom v-if="$slots.bottom" class="transition-all duration-300 *:pointer-events-auto" :class="controlsDimClass">
            <slot name="bottom" />
          </div>
        </div>
      </div>
      <!-- Slot épinglé au ROOT (hors overlay) : un panneau bas pleine largeur (sheet d'aperçu,
           bouton flottant…) s'y colle via `absolute bottom-0` au bas réel de la carte, et SUIT le
           plein écran puisqu'il est dans le sous-arbre téléporté. -->
      <div class="relative" :style="pinnedContainerStyle">
        <slot name="pinned" :is-fullscreen="isFullscreen" />
      </div>
    </div>
  </Teleport>
</template>

<style>
.mapboxgl-control-container {
  -webkit-overflow-scrolling: touch;
  -webkit-transform: translate3d(0, 0, 0);
}
.mapboxgl-ctrl-top-right .mapboxgl-ctrl,
.mapboxgl-ctrl-top-left .mapboxgl-ctrl {
  margin-top: calc(var(--spacing) * 4);
}
.mapboxgl-ctrl-bottom-right .mapboxgl-ctrl,
.mapboxgl-ctrl-bottom-left .mapboxgl-ctrl {
  margin-bottom: calc(var(--spacing) * 4);
}
.mapboxgl-ctrl-top-right .mapboxgl-ctrl,
.mapboxgl-ctrl-bottom-right .mapboxgl-ctrl {
  margin-right: calc(var(--spacing) * 4);
}
.mapboxgl-ctrl-top-left .mapboxgl-ctrl,
.mapboxgl-ctrl-bottom-left .mapboxgl-ctrl {
  margin-left: calc(var(--spacing) * 4);
}
.mapboxgl-ctrl-logo {
  display: none;
}
.mapboxgl-ctrl-group {
  border-radius: calc(infinity * 1px);
}
.mapboxgl-ctrl-group:not(:empty) {
  box-shadow: none;
}
.mapboxgl-scroll-zoom-blocker,
.mapboxgl-touch-pan-blocker {
  font-family: var(--font-sans);
  font-size: 1rem;
  line-height: 1.25;
  color: var(--color-white);
  background: color-mix(in oklab, var(--color-blue-800) 75%, transparent);
  padding: 3rem 4rem;
  z-index: 1;
  transition-duration: 200ms;
}
</style>
